import { type ReactNode, useMemo } from 'react';
import { routes } from 'routes/routes';
import { usePermissions } from 'permissions';
import { Icon } from 'ui';
import { isGivingEnabled } from 'common/validators';

export type NavigationMenuConfig = NavigationMenuConfigSections[];

export type NavigationMenuItemConfig = {
  activeIcon: ReactNode;
  alias?: string;
  href: string;
  icon: ReactNode;
  id: string;
  isHidden?: () => boolean;
  key: string;
  text: string;
};

type NavigationMenuConfigSections = {
  collapsedName?: string;
  name?: string;
  section?: NavigationMenuItemConfig[];
};

export const useNavigationMenuConfig = (): NavigationMenuConfig => {
  const { canViewVideos } = usePermissions();

  return useMemo(
    () => [
      {
        section: [
          {
            activeIcon: <Icon icon="home-4-fill" />,
            href: '/feed',
            icon: <Icon icon="home-4-line" />,
            id: 'nav-feed',
            key: '/feed',
            text: 'Home',
          },
          {
            activeIcon: <Icon icon="calendar-fill" />,
            alias: routes.events.index,
            href: routes.calendar.index,
            icon: <Icon icon="calendar-line" />,
            id: 'nav-calendar',
            key: routes.calendar.index,
            text: 'Calendar',
          },
          {
            activeIcon: <Icon icon="compass-fill" />,
            href: '/channels',
            icon: <Icon icon="compass-line" />,
            id: 'nav-channels',
            key: '/channels',
            text: 'Channels',
          },
          {
            activeIcon: <Icon icon="hand-heart-fill" />,
            href: routes.giving.now,
            icon: <Icon icon="hand-heart-line" />,
            id: 'nav-givings',
            isHidden: () => !isGivingEnabled(),
            key: routes.giving.index,
            text: 'Giving',
          },
        ],
      },
      {
        collapsedName: 'MY',
        name: 'MY STUFF',
        section: [
          {
            activeIcon: <Icon icon="bookmark-fill" />,
            href: '/bookmarks',
            icon: <Icon icon="bookmark-line" />,
            id: 'nav-bookmarks',
            key: '/bookmarks',
            text: 'Bookmarks',
          },
          {
            activeIcon: <Icon icon="classify-2-fill" />,
            href: routes.community.index,
            icon: <Icon icon="classify-2-line" />,
            id: 'nav-communities',
            key: routes.community.index,
            text: 'Communities',
          },
          {
            activeIcon: <Icon icon="notebook-2-fill" />,
            href: '/journal',
            icon: <Icon icon="notebook-2-line" />,
            id: 'nav-journal',
            key: '/journal',
            text: 'Journal',
          },
          {
            activeIcon: <Icon icon="bank-fill" />,
            alias: routes.group.index,
            href: routes.organization.index,
            icon: <Icon icon="bank-line" />,
            id: 'nav-organizations',
            key: routes.organization.index,
            text: 'Organizations',
          },
          {
            activeIcon: <Icon icon="mortarboard-fill" />,
            alias: routes.program.indexAlias,
            href: routes.program.index,
            icon: <Icon icon="mortarboard-line" />,
            id: 'nav-programs',
            key: routes.program.index,
            text: 'Programs',
          },
          {
            activeIcon: <Icon icon="video-fill" />,
            href: routes.video.index,
            icon: <Icon icon="video-line" />,
            id: 'nav-video',
            isHidden: () => !canViewVideos({}),
            key: routes.video.index,
            text: 'Videos',
          },
        ],
      },
    ],
    [canViewVideos],
  );
};
